<app-navbar></app-navbar>

<div class="study-together-layout">
  <a routerLink="/dashboard" class="back-link">
    <i class="bi bi-arrow-left"></i> Back to Dashboard
  </a>

  <h1 class="page-title">Study together</h1>
  <p class="page-subtitle">Shared tests from other pilots, plus friends when you connect.</p>

  @if (hasSharedTestNotification()) {
    <div class="panel shared-test-notification" data-testid="shared-test-notification">
      <p class="shared-test-notification-title mb-1">
        <i class="bi bi-share-fill" aria-hidden="true"></i>
        {{ socialSummary?.sharedTestNotificationTitle }}
      </p>
      @if (socialSummary?.sharedTestNotificationBody) {
        <p class="shared-test-notification-body mb-0">{{ socialSummary?.sharedTestNotificationBody }}</p>
      }
    </div>
  }

  <div class="panel filter-panel hub-filter-panel">
    <div class="filter-row">
      <div class="filter-group">
        <span class="filter-label">Section</span>
        <div class="hub-tabs" role="tablist" aria-label="Study together sections">
          <button
            type="button"
            class="hub-tab"
            [class.hub-tab-active]="activeTab === 'friends'"
            (click)="selectTab('friends')"
            data-testid="study-tab-friends"
          >
            Friends
            @if (friendsBadgeCount() > 0) {
              <span class="hub-tab-badge">{{ friendsBadgeCount() }}</span>
            }
          </button>
          <button
            type="button"
            class="hub-tab"
            [class.hub-tab-active]="activeTab === 'shared'"
            [disabled]="!hasSharedRecords()"
            (click)="selectTab('shared')"
            data-testid="study-tab-shared"
            [attr.aria-disabled]="!hasSharedRecords()"
          >
            Shared with me
            @if (sharedBadgeCount() > 0) {
              <span class="hub-tab-badge">{{ sharedBadgeCount() }}</span>
            }
          </button>
        </div>
      </div>
    </div>
  </div>

  @if (activeTab === 'friends') {
    <div class="panel hub-panel">
      @if (friendsLoading) {
        <p class="hub-empty-hint">Loading friends…</p>
      } @else {
        @if (incomingRequests().length > 0) {
          <section class="hub-section">
            <h2 class="hub-section-title">Pending requests</h2>
            <ul class="hub-list">
              @for (req of incomingRequests(); track req.friendshipId) {
                <li class="hub-list-item">
                  <span>{{ req.displayName }}</span>
                  <span class="hub-actions">
                    <button type="button" class="btn btn-sm btn-primary" (click)="acceptRequest(req.friendshipId)">Accept</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" (click)="declineRequest(req.friendshipId)">Decline</button>
                  </span>
                </li>
              }
            </ul>
          </section>
        }

        @if (outgoingRequests().length > 0) {
          <section class="hub-section">
            <h2 class="hub-section-title">Waiting for response</h2>
            <p class="hub-empty-hint mb-2">Requests stay pending until they accept, decline, or you cancel. They do not expire automatically.</p>
            <ul class="hub-list">
              @for (req of outgoingRequests(); track req.friendshipId) {
                <li class="hub-list-item">
                  <span>{{ req.displayName }}</span>
                  <span class="hub-actions">
                    <span class="text-muted me-2">Pending</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      data-testid="cancel-friend-request"
                      (click)="cancelRequest(req.friendshipId)"
                    >
                      Cancel
                    </button>
                  </span>
                </li>
              }
            </ul>
          </section>
        }

        @if (friends.length > 0) {
          <section class="hub-section">
            <h2 class="hub-section-title">Your friends</h2>
            <ul class="hub-list">
              @for (friend of friends; track friend.userId) {
                <li class="hub-list-item">
                  <span>
                    <strong>{{ friend.displayName }}</strong>
                    @if (friend.streakDay) {
                      <span class="text-muted"> · {{ friend.streakDay }} day streak</span>
                    }
                    @if (friend.trainedToday) {
                      <span class="text-success"> · trained today</span>
                    }
                  </span>
                </li>
              }
            </ul>
          </section>
        }

        <section class="hub-section">
          <h2 class="hub-section-title">Find pilots</h2>
          <div class="find-row">
            <input
              type="text"
              class="form-control"
              [(ngModel)]="findUsername"
              (ngModelChange)="findCheckOk = false; findCheckMessage = ''"
              placeholder="Enter username"
              data-testid="find-pilot-username"
            />
            <button type="button" class="btn btn-outline-secondary" (click)="checkFindUsername()" [disabled]="findCheckLoading || !findUsername.trim()">
              @if (findCheckLoading) {
                <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
              } @else {
                Check
              }
            </button>
          </div>
          @if (findCheckMessage) {
            <p class="form-text mb-0 mt-2" [class.text-success]="findCheckOk" [class.text-danger]="!findCheckOk">{{ findCheckMessage }}</p>
          }
          <button
            type="button"
            class="btn btn-primary mt-3"
            (click)="sendFindRequest()"
            [disabled]="!findCheckOk || findSubmitLoading"
            data-testid="send-friend-request"
          >
            @if (findSubmitLoading) {
              <span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
            }
            Send friend request
          </button>
        </section>

        @if (suggestions.length > 0) {
          <section class="hub-section">
            <h2 class="hub-section-title">Suggested for you</h2>
            <ul class="hub-list">
              @for (s of suggestions; track s.userId) {
                <li class="hub-list-item">
                  <span>{{ suggestionLabel(s) }}</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    (click)="inviteSuggestion(s.userId, s.displayName)"
                    [disabled]="inviteLoadingUserId === s.userId"
                  >
                    Invite
                  </button>
                </li>
              }
            </ul>
          </section>
        }
      }
    </div>
  }

  @if (activeTab === 'shared') {
    <app-shared-tests [embedded]="true" (sharedListLoaded)="loadHubData()"></app-shared-tests>
  }
</div>
